import * as Dialog from '@radix-ui/react-dialog'
import * as Menu from '@radix-ui/react-dropdown-menu'
import { useEffect, useId, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom'
import { getCollections } from '../api/client'
import type { Collection } from '../api/types'
import { anmeldemeldung, useAuth } from '../auth'
import { useCart } from '../cart'
import { useResource } from '../hooks/useResource'
import { SPRACHEN, useSprache, useT, type Sprache } from '../i18n'
import { useShopConfig } from '../shopConfig'
import { useShopPages } from '../shopPages'
import { CartDrawer } from './CartDrawer'
import { Suchfenster } from './Suchfenster'
import { Zeichen } from './Zeichen'

/** Wie viele Sammlungen in der Kopfzeile stehen. Mehr passt nicht neben Name
 *  und Werkzeuge, ohne dass die Zeile bricht — der Rest steht unter „Alle". */
const SAMMLUNGEN_IM_KOPF = 4

/**
 * Kopf, Fuß und Menü — der Rahmen, den jede Seite teilt.
 *
 * Die Kopfzeile ist eine Zeile, auf jeder Breite. Am Telefon wandern die
 * Verweise in ein Menü, statt eine zweite Zeile aufzumachen: Zwei Zeilen waren
 * 96 Pixel von 844, bevor der Laden überhaupt anfing.
 *
 * Die Verweise sind die Sammlungen des Ladens selbst und nicht „Startseite ·
 * Sammlungen · Bestellung verfolgen". Ein Kunde kommt, um Kaffee zu kaufen,
 * nicht um die Gliederung der Seite zu erkunden — und der Name des Ladens
 * links führt ohnehin zur Startseite. „Bestellung verfolgen" steht im
 * Kontomenü und im Fuß, wo man nach dem Kauf danach sucht.
 */
export function Layout({ children }: { children: ReactNode }) {
  const t = useT()
  const { shopName, theme } = useShopConfig()
  const sammlungen = useResource((signal) => getCollections(signal), [])
  const imKopf = (sammlungen.data ?? []).slice(0, SAMMLUNGEN_IM_KOPF)

  return (
    <div className="seite">
      <a className="springmarke" href="#inhalt">
        {t('layout.zumInhalt')}
      </a>

      {/* Die erste Zusage des Ladens über jeder Seite. Auf der Startseite
          stehen alle drei unter der Bühne — hier steht die eine, die an der
          Kasse zählt, auch auf der Produktseite, wo die Entscheidung fällt. */}
      {theme?.promises?.[0] && <p className="sf-ansage">{theme.promises[0]}</p>}

      <header className="sf-kopf">
        <div className="sf-kopf-innen">
          <Link className="sf-marke" to="/">
            {/* Das Logo trägt den Ladennamen als Alternativtext und nicht
                „Logo": Wer es nicht sieht, will wissen, in wessen Laden er
                steht — nicht, dass dort ein Bild hängt. */}
            {theme?.logoUrl ? <img src={theme.logoUrl} alt={shopName} /> : <span>{shopName}</span>}
          </Link>

          <nav className="sf-nav" aria-label={t('nav.aria')}>
            {imKopf.map((sammlung) => (
              <NavLink key={sammlung.id} to={`/sammlung/${sammlung.handle}`}>
                {sammlung.name}
              </NavLink>
            ))}
            <NavLink to="/suche" end>
              {t('suche.alleProdukte')}
            </NavLink>
          </nav>

          <div className="sf-werkzeuge">
            <Suchfenster />
            <Kontomenue />
            <Warenkorbknopf />
            <Menue sammlungen={sammlungen.data ?? []} />
          </div>
        </div>
      </header>

      <Anmeldehinweis />

      <main className="inhalt" id="inhalt">
        {children}
      </main>

      <CartDrawer />

      <Fuss sammlungen={sammlungen.data ?? []} />
    </div>
  )
}

/**
 * Der Warenkorb in der Kopfzeile.
 *
 * Die Zahl zählt Stücke, nicht Zeilen: „3" soll heißen, dass drei Dinge im Korb
 * liegen — dass es zwei davon zweimal gibt, sieht man beim Öffnen.
 */
function Warenkorbknopf() {
  const t = useT()
  const { anzahl, oeffnen } = useCart()

  return (
    <button
      className="sf-symbolknopf"
      type="button"
      onClick={oeffnen}
      aria-label={anzahl === 0 ? t('korb.oeffnenLeer') : t('korb.oeffnenAnzahl', { anzahl })}
    >
      <Zeichen art="korb" groesse={22} />
      {anzahl > 0 && (
        <span className="sf-zaehler" aria-hidden="true">
          {anzahl > 99 ? '99+' : anzahl}
        </span>
      )}
    </button>
  )
}

/**
 * Das Konto als Aufklappmenü — anmelden, Bestellungen, abmelden.
 *
 * Vorher stand dort ein Textknopf „Anmelden", der am Telefon den Warenkorb in
 * eine zweite Zeile drückte, und angemeldet ein Name mit einem Verweis
 * „Abmelden" daneben: zwei Dinge, die in der Kopfzeile nichts zu suchen haben,
 * bis jemand danach fragt. Ein Zeichen und ein Menü dahinter ist, was jeder
 * große Laden macht, und aus demselben Grund.
 *
 * „Bestellung verfolgen" steht auch hier. Wer als Gast bestellt hat, sucht
 * nach dem Kauf beim Konto danach — nicht in einer Navigation, die Sammlungen
 * aufzählt.
 */
function Kontomenue() {
  const t = useT()
  const { anbieter, ich, laedt, anmelden, abmelden } = useAuth()
  const { neuLaden } = useCart()
  const { pathname, search } = useLocation()
  const [meldetAb, setMeldetAb] = useState(false)

  if (laedt) return null

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button
          className="sf-symbolknopf"
          type="button"
          aria-label={
            ich.signedIn
              ? t('konto.menueAngemeldet', { name: ich.name?.trim() || ich.email || '' })
              : t('konto.menue')
          }
        >
          <Zeichen art="konto" groesse={21} />
        </button>
      </Menu.Trigger>

      <Menu.Portal>
        <Menu.Content className="sf-kontomenue" align="end" sideOffset={8}>
          {ich.signedIn && (
            <div className="sf-kontomenue-kopf">
              {/* Der Name und nicht „Mein Konto": Wer zwei Konten hat, soll
                  sehen, in welchem er gerade steckt. */}
              <strong>{ich.name?.trim() || t('konto.angemeldet')}</strong>
              {ich.email && <span>{ich.email}</span>}
            </div>
          )}

          {!ich.signedIn &&
            anbieter.map((eintrag) => (
              <Menu.Item
                key={eintrag.key}
                className="sf-kontomenue-eintrag sf-kontomenue-haupt"
                onSelect={() => anmelden(eintrag.key, pathname + search)}
              >
                {/* Bei genau einem Anbieter reicht „Anmelden". Bei mehreren
                    steht sein Satz da — „Mit Hirax Cloud anmelden" ist seiner,
                    nicht unserer. */}
                {anbieter.length === 1 ? t('konto.anmelden') : eintrag.label}
              </Menu.Item>
            ))}

          {ich.signedIn && (
            <Menu.Item asChild className="sf-kontomenue-eintrag">
              <Link to="/meine-bestellungen">{t('nav.meineBestellungen')}</Link>
            </Menu.Item>
          )}

          <Menu.Item asChild className="sf-kontomenue-eintrag">
            <Link to="/bestellung">{t('nav.bestellungVerfolgen')}</Link>
          </Menu.Item>

          {ich.signedIn && (
            <>
              <Menu.Separator className="sf-kontomenue-trenner" />
              <Menu.Item
                className="sf-kontomenue-eintrag"
                disabled={meldetAb}
                onSelect={() => {
                  if (meldetAb) return
                  setMeldetAb(true)
                  void abmelden().then(() => {
                    // Der Warenkorb des Kontos ist nicht mehr seiner: Der
                    // Server hat das Cookie mit abgeräumt. Ohne Nachladen
                    // arbeitete der nächste Klick an einem Korb, den es nicht
                    // mehr gibt.
                    neuLaden()
                    setMeldetAb(false)
                  })
                }}
              >
                {meldetAb ? t('konto.meldetAb') : t('konto.abmelden')}
              </Menu.Item>
            </>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}

/**
 * Das Menü am Telefon.
 *
 * Nur unter 900 Pixeln sichtbar (sf-nur-schmal). Es schließt sich beim Wechsel
 * der Seite von selbst: Wer eine Sammlung antippt, will die Sammlung sehen,
 * nicht das Menü, das noch darüber liegt.
 */
function Menue({ sammlungen }: { sammlungen: Collection[] }) {
  const t = useT()
  const [offen, setOffen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setOffen(false)
  }, [pathname])

  return (
    <Dialog.Root open={offen} onOpenChange={setOffen}>
      <Dialog.Trigger asChild>
        <button className="sf-symbolknopf sf-nur-schmal" type="button" aria-label={t('nav.menue')}>
          <Zeichen art="menue" groesse={22} />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="hinterlegung" />
        <Dialog.Content className="sf-menue" aria-describedby={undefined}>
          <div className="sf-menue-kopf">
            <Dialog.Title className="sf-bildschirmleser">{t('nav.menue')}</Dialog.Title>
            <span />
            <Dialog.Close asChild>
              <button className="sf-symbolknopf" type="button" aria-label={t('nav.menueSchliessen')}>
                <Zeichen art="schliessen" groesse={22} />
              </button>
            </Dialog.Close>
          </div>

          <nav aria-label={t('nav.aria')}>
            {sammlungen.map((sammlung) => (
              <Link key={sammlung.id} to={`/sammlung/${sammlung.handle}`}>
                {sammlung.name}
              </Link>
            ))}
            <Link to="/suche">{t('suche.alleProdukte')}</Link>
          </nav>

          <div className="sf-menue-fuss">
            <Link to="/bestellung">{t('nav.bestellungVerfolgen')}</Link>
            <Sprachwahl />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/**
 * Der Fuß: wer der Laden ist, wo man einkauft, wo man Hilfe findet, was er
 * rechtlich sagen muss.
 *
 * Impressum, Widerruf und Datenschutz gehören unter jede Seite — sie müssen von
 * überall aus erreichbar sein. Welche es sind, entscheidet der Laden.
 */
function Fuss({ sammlungen }: { sammlungen: Collection[] }) {
  const t = useT()
  const { shopName, theme } = useShopConfig()
  const seiten = useShopPages()
  const { ich } = useAuth()

  return (
    <footer className="sf-fuss">
      <div className="sf-fuss-innen">
        <div className="sf-fuss-spalten">
          <div className="sf-fuss-marke">
            <p className="sf-fuss-name">{shopName}</p>
            {theme?.tagline && <p>{theme.tagline}</p>}
            {theme?.footerNote && <p>{theme.footerNote}</p>}
          </div>

          <nav aria-labelledby="fuss-einkaufen">
            <h2 id="fuss-einkaufen">{t('fuss.einkaufen')}</h2>
            <ul>
              {sammlungen.slice(0, 6).map((sammlung) => (
                <li key={sammlung.id}>
                  <Link to={`/sammlung/${sammlung.handle}`}>{sammlung.name}</Link>
                </li>
              ))}
              <li>
                <Link to="/suche">{t('suche.alleProdukte')}</Link>
              </li>
            </ul>
          </nav>

          <nav aria-labelledby="fuss-service">
            <h2 id="fuss-service">{t('fuss.service')}</h2>
            <ul>
              <li>
                <Link to="/bestellung">{t('nav.bestellungVerfolgen')}</Link>
              </li>
              {ich.signedIn && (
                <li>
                  <Link to="/meine-bestellungen">{t('nav.meineBestellungen')}</Link>
                </li>
              )}
            </ul>
          </nav>

          {seiten.length > 0 && (
            <nav aria-labelledby="fuss-rechtliches">
              <h2 id="fuss-rechtliches">{t('fuss.rechtliches')}</h2>
              <ul>
                {seiten.map((seite) => (
                  <li key={seite.handle}>
                    <Link to={`/seite/${seite.handle}`}>{seite.title}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>

        <div className="sf-fuss-unten">
          <p>{t('fuss.rechte', { jahr: new Date().getFullYear(), laden: shopName })}</p>
          <p>{t('fuss.mwst')}</p>
          <Sprachwahl />
        </div>
      </div>
    </footer>
  )
}

/**
 * Was der Server über eine misslungene Anmeldung sagt.
 *
 * Er schickt den Kunden mit `?anmeldung=…` zurück. Ohne diese Zeile käme er
 * wortlos an und wüsste nicht, ob er jetzt angemeldet ist oder nicht.
 */
function Anmeldehinweis() {
  const t = useT()
  const [params] = useSearchParams()
  const text = anmeldemeldung(params.get('anmeldung'), t)
  if (!text) return null

  return (
    <p className="hinweis anmeldehinweis" role="status">
      {text}
    </p>
  )
}

/**
 * Der Sprachwähler.
 *
 * Ein Auswahlfeld und keine Reihe Flaggen: Flaggen sind Länder, keine Sprachen
 * — Englisch gehört nicht Großbritannien, und Deutsch nicht nur Deutschland.
 */
function Sprachwahl() {
  const { sprache, setzeSprache } = useSprache()
  const t = useT()
  // Eine Kennung je Stelle: Der Wähler steht im Fuß UND im Menü, und bei
  // geöffnetem Menü gäbe es sonst zwei Elemente mit derselben id — das Label
  // des zweiten zeigte dann auf das erste.
  const id = useId()

  return (
    <span className="sf-auswahl">
      <label className="sf-bildschirmleser" htmlFor={id}>
        {t('sprache.label')}
      </label>
      <select
        id={id}
        value={sprache}
        onChange={(ereignis) => setzeSprache(ereignis.target.value as Sprache)}
      >
        {SPRACHEN.map((eintrag) => (
          /* Der Name in seiner eigenen Sprache: Wer auf einer deutschen Seite
             nach Englisch sucht, sucht „English". */
          <option key={eintrag.code} value={eintrag.code}>
            {eintrag.name}
          </option>
        ))}
      </select>
    </span>
  )
}
